/**
 * @fileoverview Which plant this photograph is of.
 *
 * Photographing one plant while standing in front of another is the easiest
 * mistake this app allows, and the only cure used to be deleting a picture of
 * something that happened in June.
 *
 * It reads as a statement rather than an action: a row names the plant the
 * photo is on now, and tapping it offers the garden to choose from. It used to show "Move it to another
 * plant" over a list with the current plant missing, which told you nothing
 * about where the picture actually was and made the control look like a
 * button that had not been pressed yet.
 *
 * Choosing another turns the row amber and says where it goes on Save, which
 * is what a warning box under the dropdown used to say. It leaves the move to
 * Save,
 * which is the button that saves everything else on this screen. It used to
 * arm its own separate confirm button below the dropdown — which is how a
 * gardener could change the plant, press the obvious button, and have the
 * photo not move: the obvious button ignored it. One screen, one save.
 *
 * Where: EditPhotoDialog, for a plant's photo only (a bed's photo stays on
 * its bed).
 * API: none; the dialog moves it (PUT /photos/:plantId/:photoId/plant).
 * Settings: none.
 * Words (en/es): plants.photo.onPlant, plants.photo.whichPlant,
 * plants.photo.movesTo.
 *
 * @module MoveToPlantField
 */

import React from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { BottomPanel, PanelOption } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { Plant } from '@/shared/types/plant';
import { cn } from '@/lib/utils';
import { PHOTO_FIELD } from './photoField';
import { plantWithBed } from '@/shared/lib/plantName';

/** The garden, where the photo is, and where it is going. */
interface MoveToPlantFieldProps {
  /** Every plant in the garden, including the one the photo is on. */
  plants: Plant[];
  /** The plant the photo belongs to right now. */
  currentPlantObjId: number;
  /** The plant the photo will move to when saved, or null to leave it. */
  chosen: number | null;
  /** Called with the plant chosen, or null when it is the one it is on already. */
  onChoose: (plantObjId: number | null) => void;
  /** While the dialog is busy. */
  disabled: boolean;
}

/**
 * The "On plant" row, and the list of the garden it opens.
 *
 * @param props - MoveToPlantFieldProps.
 */
export const MoveToPlantField: React.FC<MoveToPlantFieldProps> = ({
  plants, currentPlantObjId, chosen, onChoose, disabled,
}) => {
  const { reactTranslate: t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  // Nowhere to move it to, so nothing to offer.
  if (plants.length < 2) return null;

  const showing = chosen ?? currentPlantObjId;
  const plant = plants.find((p) => p.plant_obj_id === showing);
  const moving = chosen !== null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={cn(
          PHOTO_FIELD, 'shrink-0 flex-row items-center gap-3',
          moving && 'border-warning/40 bg-warning-tint hover:bg-warning-tint',
        )}
      >
        <span className="min-w-0 grow">
          <span className={cn('block text-xs', moving ? 'text-warning' : 'text-muted-foreground')}>
            {moving ? t('plants.photo.movesTo') : t('plants.photo.onPlant')}
          </span>
          <span className="block truncate text-[0.9375rem] font-medium">{plant ? plantWithBed(plant) : ''}</span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      <BottomPanel open={open} onOpenChange={setOpen} title={t('plants.photo.whichPlant')}>
        <div role="listbox" aria-label={t('plants.photo.whichPlant')} className="grid">
          {plants.map((each) => (
            <PanelOption
              key={each.plant_obj_id}
              selected={each.plant_obj_id === showing}
              onClick={() => {
                // Back to where it already is: nothing to move.
                onChoose(each.plant_obj_id === currentPlantObjId ? null : each.plant_obj_id);
                setOpen(false);
              }}
            >
              <span className="grow truncate">{plantWithBed(each)}</span>
              {each.plant_obj_id === showing && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
            </PanelOption>
          ))}
        </div>
      </BottomPanel>
    </>
  );
};
